// Per-resource duplicate detection. Each function calls a server-side
// RPC (defined in supabase/migrations/014_duplicate_detection.sql) that
// returns up to five candidate rows from the published table, ranked by
// trigram similarity to the caller-supplied probe.
//
// All five functions:
//   - return [] if the primary signal is shorter than MIN_LEN, so we
//     don't query Postgres on every keystroke from one-character input
//   - swallow Supabase errors and log to console — the warning panel is
//     a nice-to-have, never a blocker for the submit button
//   - accept an `excludeId` for edit forms so a scholar editing their
//     own row doesn't see themselves in the similar-entries list
//
// The threshold (server default 0.6) is intentionally not exposed in
// the JS layer for now — tuning lives in the SQL function so a single
// change propagates everywhere without a frontend deploy.

import { supabase } from './supabase.js';

const MIN_LEN = 4;

async function callRpc(name, args) {
  const { data, error } = await supabase.rpc(name, args);
  if (error) {
    // eslint-disable-next-line no-console
    console.warn(`[hc] ${name} rpc failed:`, error.message);
    return [];
  }
  return data ?? [];
}

export async function findSimilarBibliography({ title, year, excludeId } = {}) {
  if (!title || title.trim().length < MIN_LEN) return [];
  return callRpc('find_similar_bibliography', {
    p_title: title.trim(),
    p_year: typeof year === 'number' ? year : (year ? parseInt(year, 10) : null),
    p_exclude_id: excludeId ?? null,
  });
}

export async function findSimilarScholars({ name, email, excludeId } = {}) {
  if (!name || name.trim().length < MIN_LEN) return [];
  return callRpc('find_similar_scholars', {
    p_name: name.trim(),
    p_email: email?.trim() || null,
    p_exclude_id: excludeId ?? null,
  });
}

export async function findSimilarOrganizations({ name, website, excludeId } = {}) {
  if (!name || name.trim().length < MIN_LEN) return [];
  return callRpc('find_similar_organizations', {
    p_name: name.trim(),
    p_website: website?.trim() || null,
    p_exclude_id: excludeId ?? null,
  });
}

export async function findSimilarTeaching({ title, url, excludeId } = {}) {
  if (!title || title.trim().length < MIN_LEN) return [];
  return callRpc('find_similar_teaching', {
    p_title: title.trim(),
    p_url: url?.trim() || null,
    p_exclude_id: excludeId ?? null,
  });
}

export async function findSimilarResearchTools({ name, url, excludeId } = {}) {
  if (!name || name.trim().length < MIN_LEN) return [];
  return callRpc('find_similar_research_tools', {
    p_name: name.trim(),
    p_url: url?.trim() || null,
    p_exclude_id: excludeId ?? null,
  });
}

export async function findSimilarEngagements({ title, url, excludeId } = {}) {
  if (!title || title.trim().length < MIN_LEN) return [];
  return callRpc('find_similar_broader_engagements', {
    p_title: title.trim(),
    p_url: url?.trim() || null,
    p_exclude_id: excludeId ?? null,
  });
}

export async function findSimilarWhatsNew({ title, dateStart, excludeId } = {}) {
  if (!title || title.trim().length < MIN_LEN) return [];
  return callRpc('find_similar_whatsnew', {
    p_title: title.trim(),
    p_date_start: dateStart || null,
    p_exclude_id: excludeId ?? null,
  });
}
